/**
 * UnderwaterOverlay — the SUBMERGED-CAMERA screen effect (the games-standard
 * underwater recipe: Ilett's "Something Fishy"/"Sinking Feeling", Cyanilux's
 * water breakdown):
 *
 *  - depth-graded ABSORPTION veil (red dies first; the view converges to
 *    the deep-water color as the camera sinks),
 *  - near-surface CAUSTIC shimmer (Voronoi bands that fade with depth),
 *  - a soft VIGNETTE closing in toward the deep color.
 *
 * Implemented as a fullscreen CLIP-SPACE triangle (vertexNode returns the
 * clip vec4 directly — the r182 contract) BLENDED over the live render —
 * deliberately NOT a scene-copy re-shade: ViewportTextureNode copies once
 * per frame at node-update time, and inside a post pipeline (HDR target +
 * shadow passes) that copy can be the CLEARED target or a stale frame — the
 * submerged view then became a frozen sky-colored wash instead of the world
 * (2026-07-30). A transparent veil grades whatever is really on screen, on
 * every backend, per-view in XR. Draw it LAST (renderOrder 9999, no depth).
 *
 * PER BODY + THE WATERLINE (shader gaps R1, 2026-09-29): the deep / shallow colours and the absorption are UNIFORMS
 * (`uDeepColor`, `uShallowColor`, `uAbsorption`; the constructor's config seeds them, so a caller that never touches
 * them draws exactly as before) — a lake or a river dives into its own water. `uWaterline` = (l0, l1, l2, mode) is the
 * near-plane waterline (waterLook.nearPlaneWaterline): mode 0 = the whole view (the old switch), mode 1 = the veil only
 * below the line, plus a thin MENISCUS band along it (Crest's `UnderwaterMeniscus.shader`: a tinted line where the
 * surface meets the lens).
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../../render/post/mrtOverlay.js';
import { MENISCUS_WIDTH, WATERLINE_EDGE } from './waterLook.js';
import {
  Fn,
  If,
  clamp,
  dot,
  exp,
  abs,
  float,
  mix,
  mx_worley_noise_float,
  positionLocal,
  screenUV,
  smoothstep,
  uniform,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';

export interface UnderwaterOverlayConfig {
  /** Deep-water color the view converges to. Default 0x03212e. */
  deepColor?: THREE.ColorRepresentation;
  /** Shallow scatter tint multiplied into the near view. Default 0x2e8b8b. */
  shallowColor?: THREE.ColorRepresentation;
  /** Beer–Lambert extinction per channel (1/m). Default water-ish r-first. */
  absorption?: { r: number; g: number; b: number };
  /** Screen-UV distortion amplitude. Default 0.011. */
  distortionStrength?: number;
  /** Distortion band count across the screen. Default 6. */
  distortionScale?: number;
  /** Wobble speed multiplier. Default 1. */
  speed?: number;
  /** Caustic shimmer strength near the surface. Default 0.14. */
  causticStrength?: number;
}

export class UnderwaterOverlay {
  /** Fullscreen clip-space triangle; add to the scene and toggle visible. */
  readonly mesh: THREE.Mesh;
  /** Shared water clock (seconds). */
  readonly uTime = uniform(0);
  /** Camera depth below the water surface (m) — drives the grading. */
  readonly uDepth = uniform(0);
  /** Master fade 0..1 (lets callers ease the effect in over a few frames). */
  readonly uIntensity = uniform(1);
  /** The deep colour the view converges to / the shallow scatter tint / the extinction per channel (1/m) — per body. */
  readonly uDeepColor = uniform(new THREE.Color(0x03212e));
  readonly uShallowColor = uniform(new THREE.Color(0x2e8b8b));
  readonly uAbsorption = uniform(new THREE.Vector3(0.35, 0.12, 0.08));
  /** The near-plane waterline (l0, l1, l2, mode) — mode 0 = the whole view (default), 1 = the veil below the line + the meniscus. */
  readonly uWaterline = uniform(new THREE.Vector4(0, 0, 0, 0));
  /** The water / air edge (screen heights) and the meniscus band's half-width. */
  readonly uWaterlineEdge = uniform(WATERLINE_EDGE);
  readonly uMeniscusWidth = uniform(MENISCUS_WIDTH);

  constructor(config: UnderwaterOverlayConfig = {}) {
    this.uDeepColor.value.set(config.deepColor ?? 0x03212e);
    this.uShallowColor.value.set(config.shallowColor ?? 0x2e8b8b);
    const ab = config.absorption ?? { r: 0.35, g: 0.12, b: 0.08 };
    this.uAbsorption.value.set(ab.r, ab.g, ab.b);
    const deep = this.uDeepColor, shallow = this.uShallowColor, absorption = this.uAbsorption;
    const uWl = this.uWaterline, uWlE = this.uWaterlineEdge, uMen = this.uMeniscusWidth;
    const speed = config.speed ?? 1;
    const causticStrength = config.causticStrength ?? 0.14;

    // One triangle covering clip space — no camera parenting, works per-eye.
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3),
    );
    // Dummy +Z normals — the overlay is unlit, but TSL's normal accessor
    // warns (then zero-fills) on any geometry without the attribute.
    geometry.setAttribute(
      'normal',
      new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 0, 0, 1]), 3),
    );

    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthTest = false;
    material.depthWrite = false;
    material.fog = false;
    markMrtOverlay(material); // a screen tint, not a surface

    const uTime = this.uTime;
    const uDepth = this.uDepth;
    const uIntensity = this.uIntensity;

    material.vertexNode = Fn(() => vec4(positionLocal.x, positionLocal.y, 0, 1))();

    material.colorNode = Fn(() => {
      const t = uTime.mul(speed);
      const suv = screenUV.toVar('uwSuv'); // read inside the gated shimmer and after it (THE LAZY-VAR LAW)

      // ── Depth-graded absorption veil: Beer–Lambert over the water column
      // between the camera and the world — red dies first, the veil deepens
      // and blues as the camera sinks. The LIVE scene stays visible through
      // it (alpha), never replaced by a copy.
      const column = float(2.0).add(clamp(uDepth, 0, 60).mul(0.5));
      const transmit = exp(vec3(absorption).negate().mul(column));
      const scatter = mix(vec3(shallow), vec3(deep), smoothstep(0, 30, uDepth));

      // ── Caustic shimmer near the surface (fades out by ~12 m down).
      // WHY gated: the gain is (1 − smoothstep(1.5, 12, depth)) — exactly 0 from 12 m down, where the full-screen Worley
      // was evaluated per pixel to be multiplied by 0. A uniform branch; the result is identical.
      const shimmer = vec3(0, 0, 0).toVar('uwShimmer');
      If(uDepth.lessThan(12.0), () => {
        const causticUv = vec3(suv.x.mul(9), suv.y.mul(9).add(t.mul(0.22)), t.mul(0.31));
        const caustic = mx_worley_noise_float(causticUv);
        const causticGain = float(causticStrength)
          .mul(float(1).sub(smoothstep(1.5, 12.0, uDepth)));
        shimmer.assign(vec3(0.7, 0.9, 1.0).mul(caustic.mul(caustic).mul(causticGain)));
      });
      let veil = scatter.add(shimmer) as unknown as ReturnType<typeof vec3>;

      // ── Vignette: edges close toward the deep color (pressure feel).
      const off = suv.sub(vec2(0.5, 0.5));
      const edge = smoothstep(0.12, 0.62, dot(off, off));
      veil = mix(veil, vec3(deep), edge.mul(0.6)) as unknown as typeof veil;

      // Coverage: how much the veil obscures — grows with depth + vignette,
      // capped so the world never fully disappears behind it.
      const cover = clamp(
        float(1).sub(transmit.g).mul(0.72).add(edge.mul(0.18)),
        0.08,
        0.85,
      );
      // The waterline (mode 1): the veil only where the near-plane point is under the water, and the meniscus —
      // a thin dark-teal line where the surface meets the lens (Crest's recipe), on both sides of it.
      const d = uWl.x.add(uWl.y.mul(suv.x)).add(uWl.z.mul(suv.y));
      const under = mix(float(1), float(1).sub(smoothstep(uWlE.negate(), uWlE, d)), uWl.w);
      const band = float(1).sub(smoothstep(0, uMen, abs(d))).mul(uWl.w);
      const withLine = mix(veil, vec3(0.03, 0.07, 0.08), band);
      return vec4(withLine, clamp(cover.mul(under), 0, 1).max(band.mul(0.55)).mul(uIntensity));
    })();

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = 'UnderwaterOverlay';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 9999;
    this.mesh.visible = false;
  }

  /** The near-plane waterline (waterLook.nearPlaneWaterline's `line`), or null for the whole view. */
  setWaterline(line: readonly [number, number, number] | null): void {
    if (line) this.uWaterline.value.set(line[0], line[1], line[2], 1);
    else this.uWaterline.value.set(0, 0, 0, 0);
  }

  /** Per-frame update while submerged. */
  update(time: number, depthBelowSurface: number): void {
    this.uTime.value = time;
    this.uDepth.value = Math.max(0, depthBelowSurface);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
